import {
  KeyboardEvent,
  MouseEvent,
  useCallback,
  useEffect,
  useRef,
  useState,
} from 'react';

import ResizeObserver from 'lib/resize-observer';
import { ARROW_LEFT, ARROW_RIGHT } from 'approot/shared/ally/keyboard.hooks';
import { calculateTableFixedValuesBasedOnContainer } from './table-view.utils';
import { MIN_TABLE_COLUMN_WIDTH, Table } from './table.constants';

function roundValue(value: number) {
  return Math.round(value);
}

export const useTableResize = ({
  containerElement,
  slideScaleRatio,
  handleUpdate,
}: {
  containerElement: HTMLElement | null;
  slideScaleRatio: number;
  handleUpdate: (widths: number[], table: Table) => void;
}) => {
  const lastValidSize = useRef<number>(0);
  const [columnWidths, setColumnWidths] = useState<number[]>([]);
  const [proportionalWidths, setProportionalWidths] = useState<number[]>([]);

  const computeColumnWidths = useCallback(() => {
    setProportionalWidths(
      calculateTableFixedValuesBasedOnContainer(
        containerElement?.offsetWidth || 1000,
        columnWidths
      )
    );
  }, [columnWidths, containerElement?.offsetWidth, setProportionalWidths]);

  const updateColumnWidths = useCallback(
    (
      index: number,
      newWidth: number,
      deltaX: number,
      ratio: number,
      currentWidths: number[]
    ) => {
      const widths = [...currentWidths];
      const currentColumnNewWidth = newWidth * ratio;
      const adjacentNewWidth = widths[index + 1] / ratio - deltaX;

      // current column is too small
      if (newWidth <= MIN_TABLE_COLUMN_WIDTH) {
        if (lastValidSize.current === 0) {
          lastValidSize.current = adjacentNewWidth * ratio;
        }
        widths[index] = roundValue(MIN_TABLE_COLUMN_WIDTH * ratio);
        widths[index + 1] = roundValue(lastValidSize.current);
        return widths;
      }

      // adjacent column is too small
      if (adjacentNewWidth < MIN_TABLE_COLUMN_WIDTH) {
        if (lastValidSize.current === 0) {
          lastValidSize.current = currentColumnNewWidth;
        }
        widths[index] = roundValue(lastValidSize.current);
        widths[index + 1] = roundValue(MIN_TABLE_COLUMN_WIDTH * ratio);
        return widths;
      }

      lastValidSize.current = 0;
      widths[index] = roundValue(currentColumnNewWidth);
      widths[index + 1] = roundValue(adjacentNewWidth * ratio);
      return widths;
    },
    []
  );

  const handleResize = useCallback(
    (
      index: number,
      newWidth: number,
      deltaX: number,
      ratio: number,
      table: Table,
      currentWidths: number[]
    ) => {
      const widths = updateColumnWidths(
        index,
        newWidth,
        deltaX,
        ratio,
        currentWidths
      );
      setColumnWidths(widths);
      handleUpdate(widths, table);
    },
    [setColumnWidths, handleUpdate, updateColumnWidths]
  );

  const onTableDownEvent = useCallback(
    (e: MouseEvent, index: number, containerWidth: number, table: Table) => {
      e.preventDefault();

      const realIndex = index - 1;
      const startX = e.clientX;
      const totalWidth = columnWidths.reduce((sum, width) => sum + width, 0);
      const ratio = totalWidth / containerWidth / slideScaleRatio;
      const initialWidth = columnWidths[realIndex] / ratio || 0;

      const onMouseMove = (moveEvent: any) => {
        const deltaX = moveEvent.clientX - startX;
        const newWidth = Math.max(
          initialWidth + deltaX,
          MIN_TABLE_COLUMN_WIDTH
        );
        handleResize(realIndex, newWidth, deltaX, ratio, table, columnWidths);
      };

      const onMouseUp = () => {
        document.removeEventListener('pointermove', onMouseMove, false);
        document.removeEventListener('pointerup', onMouseUp, false);
      };

      document.addEventListener('pointermove', onMouseMove, false);
      document.addEventListener('pointerup', onMouseUp, false);
    },
    [columnWidths, slideScaleRatio, handleResize]
  );

  const handleResizeHandleKeyDown = useCallback(
    (
      e: KeyboardEvent<HTMLButtonElement>,
      index: number,
      containerWidth: number,
      table: Table
    ) => {
      const key = e.key;
      if (key === ARROW_RIGHT || key === ARROW_LEFT) {
        e.preventDefault();

        const realIndex = index - 1;

        // start at the scaled width
        const totalWidth = columnWidths.reduce((sum, width) => sum + width, 0);
        const ratio = totalWidth / containerWidth;
        const initialWidth = columnWidths[realIndex] / ratio || 0;
        const deltaX = key === ARROW_RIGHT ? 1 : -1;
        const newWidth = Math.max(
          initialWidth + deltaX,
          MIN_TABLE_COLUMN_WIDTH
        );
        handleResize(realIndex, newWidth, deltaX, ratio, table, columnWidths);
      }
    },
    [columnWidths, handleResize]
  );

  const setTableColumnWidths = useCallback(
    (widths: number[]) => {
      setColumnWidths(widths);
    },
    [setColumnWidths]
  );

  useEffect(() => {
    const resizeObserver = new ResizeObserver(computeColumnWidths);

    if (containerElement) {
      resizeObserver.observe(containerElement);
    }

    return () => {
      if (containerElement) {
        resizeObserver.unobserve(containerElement);
      }
    };
  }, [containerElement, computeColumnWidths]);

  return {
    columnWidths,
    proportionalWidths,
    onTableDownEvent,
    setTableColumnWidths,
    handleResizeHandleKeyDown,
  };
};
